import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Grid, Paper } from '@mui/material';
import { People as PeopleIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { getData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";

function PayInfo({ payPeriod }) {
    const [payrollInfo, setPayrollInfo] = useState(null);
    const [loading, setLoading] = useState(false);

    const getShortMonth = (fullMonth) => {
        return new Date(`${fullMonth} 1, 2020`).toLocaleString("en-US", {
            month: "short",
        });
    };

    useEffect(() => {
        const fetchPayrollInfo = async () => {
            if (!payPeriod) return;
            const [monthName, year] = payPeriod.split(" ");
            const shortMonth = getShortMonth(monthName);

            setLoading(true);
            try {
                const response = await getData(API_ENDPOINTS.getBatchPayrollSummary(year, shortMonth));
                if (response?.statusCode === 200) {
                    setPayrollInfo(response.result);
                }
            } catch (error) {
                setPayrollInfo(null);
            } finally {
                setLoading(false);
            }
        };
        fetchPayrollInfo();
    }, [payPeriod]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress size={30} />
            </Box>
        );
    }

    if (!payrollInfo) {
        return <Typography sx={{ p: 2 }} color="textSecondary">No data found for this period.</Typography>;
    }

    // Helper to render stats cards
    const StatCard = ({ title, value, icon, color }) => (
        <Paper elevation={0} sx={{
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2
        }}>
            <Box sx={{
                display: 'flex',
                p: 1,
                borderRadius: 1.5,
                bgcolor: `${color}15`,
                color: color
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: 'block' }}>
                    {title}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                    {value}
                </Typography>
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ p: 0.5 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                    Payroll Overview
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Details for the billing cycle of {payPeriod}
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total Records"
                        value={payrollInfo.totalRecords}
                        icon={<PeopleIcon />}
                        color="#2196f3"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Paid Employees"
                        value={payrollInfo.payedCount}
                        icon={<CheckCircleIcon />}
                        color="#4caf50"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Unpaid Employees"
                        value={payrollInfo.notPayedCount}
                        icon={<CancelIcon />}
                        color="#f44336"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Working Days"
                        value={payrollInfo.daysInMonth}
                        icon={<CalendarIcon />}
                        color="#ff9800"
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export default PayInfo;